import { ChatBotResponse, ChatBotFormAnswer, ChatFormItem, ChatItem, ChatItemForm } from 'app/services/chatBot';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { TopicSource, TopicType } from 'app/copied/constants/common';
import { PosthogEventName } from 'app/copied/constants/PosthogEventName';

import { ChatHandlerParams } from '../types/chat';
import { createVirtualAssistantActions } from './virtualAssistantActions';

export interface State {
  chatItems: ChatItem[];
  hasStartedVirtualAssistant: boolean;
  isTyping: boolean;
  typingAreaAvailable: boolean;
  timeouts: ReturnType<typeof setTimeout>[];
  userResponsePresent: boolean;
  isLoadingVirtualAssistant: boolean;
  latestAnswerResponse: ChatBotResponse | null;
  showTypingArea: boolean;
  uuid?: string;
  isConvAiActive: boolean;
  itemsTagInfo: { [key: string]: { tagId?: string; tagName?: string; posInResponse?: number } };
  initialMessage?: string;
  hasCalledInit: boolean;
  pendingUserMessage?: string;
  isV41: boolean;
  lastInitItemId?: string;

  cancelActiveRequests: (uuid: string) => void;
  startVirtualAssistant: (
    uuid: string,
    options: {
      action?: string;
      isV41: boolean;
      isConvoAiEnabled: boolean;
      searchQuery?: string;
    }
  ) => Promise<void>;
  displayInitialMessage: (initialMessage: string) => void;
  sendAnswer: (uuid: string, params: ChatHandlerParams, displayErrorMessage?: boolean) => Promise<void>;
  sendMessage: (uuid: string, message: string) => Promise<void>;
  dismissQuestion: (uuid: string, params: ChatHandlerParams) => Promise<void>;
  presentError: () => void;
  processResponse: (
    response: ChatBotResponse,
    options?: { topicSource?: TopicSource; queryTyped?: string; isInitResponse?: boolean }
  ) => void;
  filterNewChatItems: (response: ChatBotResponse, options?: { isInitResponse?: boolean }) => ChatItem[];
  extractAnalyticsTagInfo: (response: ChatBotResponse) => {
    [key: string]: { tagId?: string; tagName?: string; posInResponse?: number };
  };
  addResultIndexToKBEntries: (items: ChatItem[]) => ChatItem[];
  handleSpecialItemTypes: (items: ChatItem[]) => void;
  scheduleItemVisibility: (items: ChatItem[]) => void;
  clearTimeouts: () => void;
  resetVirtualAssistant: () => void;
  setShowTypingArea: (showTypingArea: boolean) => void;
  resetScrollToBottomAfterInit: () => void;

  trackAnswerSubmitted: (
    formItem: ChatFormItem,
    answer: ChatBotFormAnswer,
    isConvAiActive: boolean,
    isConvAiChatItem: boolean
  ) => void;
  trackAnswerSubmissionErrors: (formItem: ChatFormItem) => void;
  trackFormDismissed: (formItem: ChatFormItem, isConvAiActive: boolean, isConvAiChatItem: boolean) => void;
  trackQuerySubmitted: (params: { query: string; isConvAiActive: boolean }) => void;
  trackMessageSent: (
    chatItem: ChatItem,
    isConvAiActive: boolean,
    isConvAiChatItem: boolean,
    additionalProps?: object
  ) => void;
  trackFormPresented: (
    chatItem: ChatItemForm,
    isConvAiActive: boolean,
    isConvAiChatItem: boolean,
    additionalProps?: object
  ) => void;
  trackLocationResultsDisplayed: (queryZip: string, locations: string[]) => void;
  trackKBEntryClicked: ({
    queryTyped,
    resultIndex,
    topicSource,
    topicType,
    topicTitle,
    kbEntryType,
    isConvAiChatItem,
  }: {
    queryTyped: string;
    resultIndex: number;
    topicSource?: TopicSource;
    topicType?: TopicType;
    topicTitle?: string;
    kbEntryType: PosthogEventName.RelatedResourcesClicked | PosthogEventName.TopicSourceClicked;
    isConvAiChatItem: boolean;
  }) => void;
  trackItemVisibility: (item: ChatItem) => void;
  storeItemsTagInfo: (itemsTagInfo: {
    [key: string]: { tagId?: string; tagName?: string; posInResponse?: number };
  }) => void;
}

export const useVirtualAssistantStore = create<State>()(
  immer((set, get) => ({
    chatItems: [],
    hasStartedVirtualAssistant: false,
    isTyping: false,
    typingAreaAvailable: false,
    timeouts: [],
    userResponsePresent: false,
    isLoadingVirtualAssistant: false,
    latestAnswerResponse: null,
    showTypingArea: true,
    isConvAiActive: false,
    itemsTagInfo: {},
    uuid: undefined,
    hasCalledInit: false,
    pendingUserMessage: undefined,
    isV41: false,
    lastInitItemId: undefined,

    ...createVirtualAssistantActions(set, get),

    trackAnswerSubmitted: () => {},
    trackAnswerSubmissionErrors: () => {},
    trackFormDismissed: () => {},
    trackQuerySubmitted: () => {},
    trackMessageSent: () => {},
    trackFormPresented: () => {},
    trackLocationResultsDisplayed: () => {},
    trackKBEntryClicked: () => {},
    trackItemVisibility: () => {},
    storeItemsTagInfo: () => {},
  }))
);
